// eslint-disable-next-line no-restricted-imports
import {Component, PanelLifecycleContext} from 'panel';
import {NavigateFunction} from 'react-router';

import {Subscribable} from 'common/types/context/subscribable';
import {
  activatePersistedZendeskChat,
  activateZendeskChat,
  ChatDepartment,
  hideZendeskWidget,
} from 'common/report/external-widgets/zendesk';
import {UserIdentity} from 'common/report/util/user-identity-from-cookie';
import {CurrentUser, Organization} from 'common/report/queries/app-api/user-metadata-client';
import {getFilteredOrgs} from 'common/widgets/chrome/chrome-header/util';
import {Permission} from 'common/report/constants';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {spaNavManager} from 'common/report/util/spa-nav-manager';
import type {QueryContextManager} from 'common/query-context';
import {getLocation} from 'common/util/url';
import {shouldOpenInNewTab} from 'common/util/dom';

type State = {
  showSupportWidget: boolean;
  isSupportWidgetMaximized: boolean;
  isInviteUserModalOpen: boolean;
  closeInviteModalCallback: (() => void) | undefined;
};

/**
 * Allows external callsites to control certain functionality within the global sidenav component
 */
export class GlobalSidenavController implements PanelLifecycleContext, Subscribable {
  private connectedComponents: Set<Component<any>> = new Set();
  private subscriptions: Array<() => void> = [];

  public state: State = {
    showSupportWidget: false,
    isSupportWidgetMaximized: false,
    isInviteUserModalOpen: false,
    closeInviteModalCallback: undefined,
  };

  updateState(stateUpdate: Partial<State>) {
    this.state = Object.assign({}, this.state, stateUpdate);
    this.updateComponents();
  }

  hideCustomerSupportWidgets() {
    this.updateState({showSupportWidget: false});
    hideZendeskWidget();
  }

  openZendeskChat({currentUser}: {currentUser: UserIdentity}) {
    this.hideCustomerSupportWidgets();
    activateZendeskChat(ChatDepartment.Support, currentUser.name, currentUser.email);
  }

  openPersistedZendeskChat({currentUser}: {currentUser: UserIdentity}) {
    activatePersistedZendeskChat(ChatDepartment.Support, currentUser.name, currentUser.email);
  }

  openContactSupport({isSupportWidgetMaximized}: {isSupportWidgetMaximized?: boolean} = {}) {
    this.hideCustomerSupportWidgets();
    this.updateState({
      showSupportWidget: true,
      ...(typeof isSupportWidgetMaximized === `boolean` ? {isSupportWidgetMaximized} : {}),
    });
  }

  getInviteUserOrgs({currentUser}: {currentUser: CurrentUser | undefined}): Organization[] {
    const userOrganizations = currentUser?.organizations ?? {};
    const orgUrlMatch = /^\/settings\/org\/(\d+)/.exec(location.pathname);
    const orgId = (orgUrlMatch && parseInt(orgUrlMatch[1], 10)) || null;
    const orgs = getFilteredOrgs(userOrganizations, {permissions: [Permission.AddUser]});
    const org = orgs.find((o) => o.id === orgId);
    // if org id is specified by url and that org has `add_user` permission, show
    // invite modal for that org; otherwise show modal for all orgs with `add_user`
    // (user will choose org in modal)
    return org ? [org] : orgs;
  }

  openInviteUserModal({
    currentUser,
    closeCallback,
  }: {
    currentUser: CurrentUser | undefined;
    closeCallback?: () => void;
  }): void {
    const inviteUserOrgs = this.getInviteUserOrgs({currentUser});
    if (!inviteUserOrgs.length) {
      return;
    }

    this.updateState({
      isInviteUserModalOpen: true,
      closeInviteModalCallback: closeCallback,
    });
  }

  closeInviteModal(): void {
    this.state.closeInviteModalCallback?.();
    this.updateState({
      isInviteUserModalOpen: false,
      closeInviteModalCallback: undefined,
    });
  }

  initializeSpaNavManager({
    currentProject,
    navigate,
  }: {
    currentProject: CurrentProject | undefined;
    navigate: NavigateFunction;
  }): void {
    if (!currentProject) {
      return;
    }
    // multiple async entry points, use the one that's ready
    spaNavManager.initialize({
      projectId: String(currentProject.id),
      projectName: currentProject.name,
      navigate,
    });
  }

  // TODO(global-sidenav) this is ported directly from mp-chrome-header. Think if any of the logic can be removed
  // (e.g. the beforeunload logic might not be needed)
  spaNavigate({
    url,
    navigate,
    currentProject,
    queryContextManager,
    ev,
    fromAnchorlink,
    keepReportContext,
  }: {
    url: string;
    navigate: NavigateFunction;
    currentProject: CurrentProject | undefined;
    queryContextManager: QueryContextManager;
    ev?: Event;
    fromAnchorlink?: boolean;
    keepReportContext?: boolean;
  }): void {
    const urlObj = new URL(url, getLocation().origin);
    urlObj.search = getLocation().search;
    const destinationUrl = urlObj.toString();
    const currentUrlPath = location.pathname;

    // If we are keeping context between reports we must navigate via spa nav.
    if (keepReportContext) {
      this.initializeSpaNavManager({currentProject, navigate});
    }
    if (!keepReportContext) {
      queryContextManager.reset();
    }

    if (shouldOpenInNewTab(ev as any)) {
      // if anchor link, browser will do the right thing by opening in new tab anyway
      if (!fromAnchorlink) {
        window.open(destinationUrl, `_blank`);
      }
    } else {
      if (spaNavManager.isNavigable(destinationUrl, currentUrlPath)) {
        // this will be a fake page navigation by swapping the iron app js bundle and css
        // we'll ask the window's beforeunload whether we should ask the user to stay on the page
        let shouldNavigate = true;
        if (window.onbeforeunload instanceof Function) {
          const beforeUnloadEv = new CustomEvent(`beforeunload`, {cancelable: true});
          window.onbeforeunload(beforeUnloadEv);
          if (beforeUnloadEv.defaultPrevented) {
            shouldNavigate = confirm(`Changes you made may not be saved. Leave this page?`);
          }
        }

        if (shouldNavigate) {
          spaNavManager.navigate(destinationUrl, currentUrlPath, ev ?? null, keepReportContext);
        } else {
          // user said no, don't navigate
          if (ev) {
            if (ev.type === `popstate`) {
              // popstate event immediately overwrites the url even though the user didn't want to leave
              // push back the old url
              window.history.pushState(null, ``, currentUrlPath);
            } else {
              ev.preventDefault();
            }
          }
        }
      } else {
        // if an anchor link is clicked, let the browser do it's thing
        // otherwise onbeforeunload will fire twice if we also set location.href
        // see https://mixpanel.atlassian.net/browse/RNA-737
        if (!fromAnchorlink) {
          window.location.href = destinationUrl;
        }

        const handleBeforeUnload = (beforeUnloadEv: BeforeUnloadEvent) => {
          // ev.defaultPrevented == true means the user was prompted to stay or leave.
          // we have no way to know what their choice was
          // so don't do an optimistic report whiteout and select the new report tab
          // if user leaves, the right state will be shown anyway after the page loads, just a bit slower
          if (!beforeUnloadEv.defaultPrevented) {
            // optimistic update that the new page started loading instantly
            // by showing whiteout loader spinny and selecting the new report in nav
            document.querySelector(`.whiteout_loader`)?.classList.remove(`hidden`);
          }
        };

        window.addEventListener(`beforeunload`, handleBeforeUnload, {once: true});
      }
    }
  }

  subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
    return this;
  }

  bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }

  unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
    for (const subscription of this.subscriptions) {
      subscription();
    }
  }
}
